import { get, isEmpty, merge } from "lodash";
import { PlotData, Layout, PlotType } from "plotly.js";
import { doHidden, COLORWAY } from "../utils";
import { I3dFigure, I3dStructure, I3dSurface, IDisplay, IResult } from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { useMemo } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";
import AutoSizedPlot from "./AutoSizedPlot";

export interface I3dSurfaceProps extends IOutputProps {
  template: I3dSurface;
}

const renderLabel = (display: IDisplay) => {
  const label = display.label ? display.label : "";
  const info = display.info ? ` <span class="pb3-text-muted">${display.info}</span>` : "";
  return `${label}${info}`;
};

const transformData = (template: I3dSurface, result: IResult): Array<Partial<PlotData>> => {
  return template.figures
    .map(
      (f) =>
        ({
          type: "surface" as PlotType,
          opacity: 0.8,
          name: renderLabel(f),
          ...((f as I3dStructure).type === "structure"
            ? {
                x: (get(result, (f as I3dStructure).name, []) as Array<any>).map((v) => v[(f as I3dStructure).x_value]),
                y: (get(result, (f as I3dStructure).name, []) as Array<any>).map((v) => v[(f as I3dStructure).y_value]),
                z: (get(result, (f as I3dStructure).name, []) as Array<any>).map((v) => v[(f as I3dStructure).z_value]),
              }
            : {
                x: get(result, (f as I3dFigure).x_data.name, []) as Array<number>,
                y: get(result, (f as I3dFigure).y_data.name, []) as Array<number>,
                z: get(result, (f as I3dFigure).z_data.name, []) as Array<number>,
              }),
        } as Partial<PlotData>)
    )
    .filter((v) => !isEmpty(v.x) && !isEmpty(v.y) && !isEmpty(v.z));
};

const createLayout = (template: I3dSurface, result: IResult): Partial<Layout> => {
  const minimal = template.variant === "minimal";
  const [xaxis, yaxis, zaxis] = !minimal
    ? [
        template.axis?.[0] || (template.figures?.[0] as I3dFigure).x_data,
        template.axis?.[1] || (template.figures?.[0] as I3dFigure).y_data,
        template.axis?.[2] || (template.figures?.[0] as I3dFigure).z_data,
      ]
    : [];
  const [xrange, yrange, zrange] = template.range || [];
  return merge(
    {
      title: template.label,
      colorway: COLORWAY,
      showlegend: true,
      scene: {
        aspectmode: "manual" as const,
        aspectratio: {
          x: 1,
          y: 1,
          z: 1,
        },
        ...(xaxis && { xaxis: { title: `${xaxis.label} ${xaxis.info}`.trim() } }),
        ...(yaxis && { yaxis: { title: `${yaxis.label} ${yaxis.info}`.trim() } }),
        ...(zaxis && { zaxis: { title: `${zaxis.label} ${zaxis.info}`.trim() } }),
      },
    },
    xaxis && { xaxis: { title: renderLabel(xaxis) } },
    yaxis && { yaxis: { title: renderLabel(yaxis) } },
    zaxis && { zaxis: { title: renderLabel(zaxis) } },
    xrange && { xaxis: { range: get(result, xrange) } },
    yrange && { yaxis: { range: get(result, yrange) } },
    zrange && { zaxis: { range: get(result, zrange) } }
  );
};

const N3dSurface = (props: I3dSurfaceProps) => {
  const { template, result, metadata } = props;
  const data = useMemo(() => transformData(template, result), [template, result]);
  const layout = useMemo(() => createLayout(template, result), [template, result]);
  const refreshing = isTemplateRefreshing(template, metadata, props.mutators.getPath);

  if (doHidden(props)) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className={template.height ? "chart-container" : "large-chart-container"}
        height={template.height || 800}
        label={template.label || template.name}
        variant="plot"
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  return (
    <RefreshingState refreshing={refreshing}>
    <div
      className={template.height ? "chart-container" : "large-chart-container"}
      {...(template.height && { style: { height: `${template.height}px` } })}
    >
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={layout}
        config={{
          displaylogo: false,
          // scrollZoom: false,
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
        }}
      />
    </div>
    </RefreshingState>
  );
};

export default N3dSurface;
